import { useState, useEffect } from "react";
import { useDarkMode } from "@/hooks/use-dark-mode";

interface AppLoadingScreenProps {
  /** When true, shows a "Server is starting" message instead of the generic slow-server message. */
  isServerStarting?: boolean;
}

export function AppLoadingScreen({ isServerStarting = false }: AppLoadingScreenProps) {
  const dark = useDarkMode();
  const [logoError, setLogoError] = useState(false);
  const [slowServer, setSlowServer] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlowServer(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  const bg = dark ? "#0f172a" : "#ffffff";
  const trackBg = dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)";
  const logoSrc = dark ? "/nashid-logo-white.png" : "/nashid-logo-blue.png";
  const textColor = dark ? "#94a3b8" : "#64748b";

  return (
    <>
      <style>{`
        @keyframes nashid-sweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .nashid-sweep {
          animation: nashid-sweep 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
      `}</style>
      <div
        role="status"
        aria-busy="true"
        aria-live="polite"
        data-testid="loading-session"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: bg,
        }}
      >
        <span className="sr-only">{/* i18n-ignore */}Loading…</span>

        {logoError ? (
          <span
            data-testid="loading-fallback"
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: dark ? "#f8fafc" : "#1e3a5f",
              marginBottom: 20,
              userSelect: "none",
            }}
          >
            Nashid
          </span>
        ) : (
          <img
            src={logoSrc}
            alt="Nashid"
            style={{ width: 80, marginBottom: 20, userSelect: "none" }}
            draggable={false}
            onError={() => setLogoError(true)}
          />
        )}

        <div
          style={{
            width: 180,
            height: 3,
            borderRadius: 2,
            background: trackBg,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            className="nashid-sweep"
            style={{
              position: "absolute",
              inset: 0,
              width: "25%",
              height: "100%",
              borderRadius: 2,
              background: "#F97316",
            }}
          />
        </div>

        {(isServerStarting || slowServer) && (
          <div
            data-testid="loading-slow-server"
            style={{
              marginTop: 24,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span
              data-testid={isServerStarting ? "loading-server-starting" : undefined}
              style={{
                fontSize: 14,
                color: textColor,
                textAlign: "center",
              }}
            >
              {isServerStarting ? "Server is starting, please wait…" : "Still connecting to the server…"}
            </span>
            <button
              data-testid="loading-retry-button"
              onClick={() => window.location.reload()}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: "6px 18px",
                borderRadius: 6,
                border: `1px solid ${dark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"}`,
                background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                color: dark ? "#f1f5f9" : "#1e3a5f",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </>
  );
}
